import { ApolloError } from "@apollo/client";
import { DEFAULT_ERROR_IMAGE } from "./WellnessPlanErrorPage";
import { WELLNESS_PLAN_ERROR_CODES } from "./wellnessPlanErrorCodes";

export const DEFAULT_ERROR_TITLE =
  "An error occurred while processing your payment";
export const DEFAULT_ERROR_DESCRIPTION =
  "Please try again later or contact support.";

/**
 * Which primary CTA the unified error screen
 * should show for a given error code.
 * The container maps these keys to concrete
 * handlers (navigation / modal / state),
 * since the action depends on runtime context (petId, billing cycle, etc).
 */
export type WellnessPlanErrorCtaKey =
  | "EXPLORE_PLAN"
  | "GO_TO_ACCOUNT"
  | "ADD_PET"
  | "SELECT_A_PLAN"
  | "TRY_AGAIN"
  | "NONE";

export interface IWellnessPlanErrorConfig {
  imageSrc: string;
  ctaKey: WellnessPlanErrorCtaKey;
}

const ERROR_CONFIG: Record<string, IWellnessPlanErrorConfig> = {
  [WELLNESS_PLAN_ERROR_CODES.ALREADY_SUBSCRIBED]: {
    imageSrc: DEFAULT_ERROR_IMAGE,
    ctaKey: "EXPLORE_PLAN",
  },
  [WELLNESS_PLAN_ERROR_CODES.INVALID_PHONE]: {
    imageSrc: DEFAULT_ERROR_IMAGE,
    ctaKey: "GO_TO_ACCOUNT",
  },
  [WELLNESS_PLAN_ERROR_CODES.NOT_CONFIGURED]: {
    imageSrc: "/assets/images/vet-with-dog.svg",
    ctaKey: "NONE",
  },
  [WELLNESS_PLAN_ERROR_CODES.PET_NOT_FOUND]: {
    imageSrc: "/assets/images/no-orders.svg",
    ctaKey: "ADD_PET",
  },
  [WELLNESS_PLAN_ERROR_CODES.PLAN_NOT_FOUND]: {
    imageSrc: "/assets/images/dog-with-tennis-balls.svg",
    ctaKey: "SELECT_A_PLAN",
  },
};

const DEFAULT_CONFIG: IWellnessPlanErrorConfig = {
  imageSrc: DEFAULT_ERROR_IMAGE,
  ctaKey: "TRY_AGAIN",
};

/**
 * Returns the image + CTA descriptor for an error code. Codes with no dedicated
 * entry (raw Square codes, SUBSCRIPTION_NOT_FOUND, USER_NOT_FOUND, unknown,
 * undefined) fall back to the default Try Again config.
 *
 * @param {string} [code] - The backend errorCode (undefined when none).
 * @return {IWellnessPlanErrorConfig} Image source and CTA key for the screen.
 */
export const getWellnessPlanErrorConfig = (
  code?: string,
): IWellnessPlanErrorConfig => {
  if (code && ERROR_CONFIG[code]) {
    return ERROR_CONFIG[code];
  }
  return DEFAULT_CONFIG;
};

/**
 * Semantic, client-side error situations the wellness flow can render before
 * (or instead of) a backend error. Each maps to a fixed title/description +
 * image + CTA so the copy stays consistent across the selection and payment
 * containers.
 */
export type WellnessPlanLocalErrorKind =
  | "PET_NOT_FOUND"
  | "PLAN_NOT_FOUND"
  | "SERVICE_UNAVAILABLE"
  | "NO_BIRTHDATE"
  | "NO_PLANS_AVAILABLE"
  | "GENERIC";

export interface IWellnessPlanErrorContent {
  title: string;
  description: string;
  imageSrc: string;
  ctaKey: WellnessPlanErrorCtaKey;
}

const PET_NOT_FOUND_IMAGE = "/assets/images/no-orders.svg";
const PLAN_NOT_FOUND_IMAGE = "/assets/images/dog-with-tennis-balls.svg";

const LOCAL_ERROR_CONTENT: Record<
  WellnessPlanLocalErrorKind,
  IWellnessPlanErrorContent
> = {
  PET_NOT_FOUND: {
    title: "Pet not found",
    description:
      "We couldn't find this pet on your account, make sure they've " +
      "been added to your profile, then try again.",
    imageSrc: PET_NOT_FOUND_IMAGE,
    ctaKey: "ADD_PET",
  },
  PLAN_NOT_FOUND: {
    title: "Wellness plan not found",
    description:
      "The plan you selected is no longer available or couldn't be " +
      "loaded. Please select a different plan.",
    imageSrc: PLAN_NOT_FOUND_IMAGE,
    ctaKey: "SELECT_A_PLAN",
  },
  SERVICE_UNAVAILABLE: {
    title: "Wellness plans are unavailable",
    description:
      "The Wellness Plan service is temporarily unavailable. Please " +
      "try again later.",
    imageSrc: PLAN_NOT_FOUND_IMAGE,
    ctaKey: "NONE",
  },
  NO_BIRTHDATE: {
    title: "Pet's date of birth is required",
    description:
      "Add your pet's date of birth to their profile to enroll in a " +
      "Wellness Plan.",
    imageSrc: PET_NOT_FOUND_IMAGE,
    ctaKey: "GO_TO_ACCOUNT",
  },
  NO_PLANS_AVAILABLE: {
    title: "No wellness plans available",
    description:
      "There are no wellness plans available for your pet at this time.",
    imageSrc: PLAN_NOT_FOUND_IMAGE,
    ctaKey: "NONE",
  },
  GENERIC: {
    title: "Oops...something went wrong!",
    description: DEFAULT_ERROR_DESCRIPTION,
    imageSrc: DEFAULT_ERROR_IMAGE,
    ctaKey: "NONE",
  },
};

/**
 * Returns the full title/description/image/CTA descriptor for a client-side
 * wellness error situation.
 *
 * @param {WellnessPlanLocalErrorKind} kind - The local error situation.
 * @return {IWellnessPlanErrorContent} Copy, image and CTA for the screen.
 */
export const getWellnessPlanLocalError = (
  kind: WellnessPlanLocalErrorKind,
): IWellnessPlanErrorContent => LOCAL_ERROR_CONTENT[kind];

export interface IParsedWellnessPlanError {
  code?: string;
  title: string;
  description: string;
}

const stripExceptionPrefix = (text: string): string =>
  text.replace(/^\s*[A-Za-z]*Exception:\s*/, "").trim();

const splitIntoSentences = (
  text: string,
): { title: string; description: string } => {
  const match = text.match(/^(.*?[.!?])\s+([\s\S]*)$/);
  if (match && match[2].trim()) {
    return { title: match[1].trim(), description: match[2].trim() };
  }
  return { title: text.trim(), description: "" };
};

/**
 * Parses an Apollo error into a title/description + errorCode for the unified
 * error screen. Reads errorCode from extensions.errorCode and the human text
 * from extensions.stacktrace[0] (non-prod) or the GraphQL message. Falls back
 * to a default title/description when the text is missing or unparseable.
 *
 * @param {ApolloError | null} [error] - The Apollo error to parse.
 * @return {IParsedWellnessPlanError} The parsed errorCode, title, description.
 */
export const parseWellnessPlanError = (
  error?: ApolloError | null,
): IParsedWellnessPlanError => {
  const graphQLError = error?.graphQLErrors?.[0];
  const extensions = graphQLError?.extensions as
    | { errorCode?: string; stacktrace?: string[] }
    | undefined;

  const code =
    typeof extensions?.errorCode === "string" ? extensions.errorCode : undefined;

  const rawText =
    extensions?.stacktrace?.[0] || graphQLError?.message || error?.message || "";

  const cleaned = stripExceptionPrefix(rawText);

  if (!cleaned) {
    return {
      code,
      title: DEFAULT_ERROR_TITLE,
      description: DEFAULT_ERROR_DESCRIPTION,
    };
  }

  const { title, description } = splitIntoSentences(cleaned);

  return {
    code,
    title: title || DEFAULT_ERROR_TITLE,
    description: description || DEFAULT_ERROR_DESCRIPTION,
  };
};
